<template>
  <div class="rspv-page">
    <div class="rspv-filters">
      <div class="rspv-filters__inner">
        <v-autocomplete
          v-if="!scopedPartnerId"
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          label="Partner"
          outlined
          dense
          hide-details
          clearable
          :loading="loadingPartners"
          class="rspv-filters__field"
          @keyup.enter="search"
        />

        <v-select
          v-model="form.status"
          :items="statusOptions"
          label="Status"
          outlined
          dense
          hide-details
          class="rspv-filters__field"
        />

        <v-btn
          class="accent white--text"
          :disabled="!form.partner_id"
          :loading="loading"
          @click="search"
        >
          Search
        </v-btn>
      </div>
    </div>

    <!-- Nothing searched yet -->
    <v-card v-if="!applied.partner_id" outlined class="rspv-state rspv-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-account-search-outline</v-icon>
      <h3 class="rspv-state__title">Select a partner</h3>
      <p class="rspv-state__text">
        Pick a partner above and hit Search to see their fortnightly revenue splits.
      </p>
    </v-card>

    <!-- Loading -->
    <div v-else-if="loading" class="rspv-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rspv-state__text">Loading revenue splits…</p>
    </div>

    <!-- Content -->
    <template v-else-if="periods.length">
      <!-- Totals -->
      <div class="rspv-totals">
        <div class="rspv-total">
          <span class="rspv-total__label">Paid Out</span>
          <span class="rspv-total__value rspv-total__value--success">
            {{ currency(totals.settled) }}
          </span>
        </div>
        <div class="rspv-total">
          <span class="rspv-total__label">Awaiting Amount</span>
          <span class="rspv-total__value rspv-total__value--warning">
            {{ currency(totals.pending) }}
          </span>
        </div>
        <div class="rspv-total">
          <span class="rspv-total__label">Pending Periods</span>
          <span class="rspv-total__value">{{ totals.pendingCount }}</span>
        </div>
      </div>

      <!-- Fortnight table -->
      <div class="rspv-details-section">
        <h3 class="rspv-details-title">
          Fortnight Records
          <span class="rspv-details-title__count">
            ({{ visiblePeriods.length }} of {{ periods.length }})
          </span>
        </h3>

        <v-card v-if="!visiblePeriods.length" outlined class="rspv-state rspv-state--card">
          <v-icon size="36" color="grey lighten-1">mdi-filter-off-outline</v-icon>
          <p class="rspv-state__text">No fortnights match this status filter.</p>
        </v-card>

        <v-data-table
          v-else
          :headers="tableHeaders"
          :items="periodsWithKey"
          item-key="_key"
          :expanded.sync="expandedItems"
          show-expand
          dense
          hide-default-footer
          :items-per-page="-1"
          class="rspv-data-table"
        >
          <!-- Period Column -->
          <template v-slot:item.period="{ item }">
            <span class="rspv-row__period">{{ periodTitle(item) }}</span>
          </template>

          <!-- Share % Column -->
          <template v-slot:item.share_percentage="{ item }">
            {{ percentOrDash(item.share_percentage) }}
          </template>

          <!-- Base Amount Column -->
          <template v-slot:item.base_amount="{ item }">
            {{ currencyOrDash(item.base_amount) }}
          </template>

          <!-- GST % Column -->
          <template v-slot:item.gst_percentage="{ item }">
            {{ percentOrDash(item.gst_percentage) }}
          </template>

          <!-- GST Amount Column -->
          <template v-slot:item.gst_amount="{ item }">
            {{ currencyOrDash(item.gst_amount) }}
          </template>

          <!-- GST Type Column -->
          <template v-slot:item.gst_type="{ item }">
            {{ item.gst_type || "—" }}
          </template>

          <!-- Total Column -->
          <template v-slot:item.total_amount="{ item }">
            <span class="rspv-td--strong">{{ currency(item.total_amount) }}</span>
          </template>

          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              label
              :color="isSettled(item) ? 'success' : 'warning'"
              text-color="white"
            >
              {{ isSettled(item) ? "Paid out" : "Pending" }}
            </v-chip>
          </template>

          <!-- Expanded Row Details -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="rspv-td rspv-td--expanded">
              <div class="rspv-expanded">
                <div class="rspv-section">
                  <h6 class="rspv-section__title">Transaction Details</h6>

                  <div v-if="!isSettled(item)" class="rspv-empty-txn">
                    <v-icon small color="warning" class="mr-2">
                      mdi-alert-circle-outline
                    </v-icon>
                    <span>No payout recorded for this fortnight yet.</span>
                  </div>

                  <div v-else class="rspv-details-grid">
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Transaction ID</span>
                      <span class="rspv-detail__value">
                        {{ item.transaction_id || "—" }}
                      </span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Transaction Date</span>
                      <span class="rspv-detail__value">
                        {{ item.transaction_date | formatDate }}
                      </span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Bank Name</span>
                      <span class="rspv-detail__value">{{ item.bank_name || "—" }}</span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Bank Account</span>
                      <span class="rspv-detail__value rspv-detail__value--code">
                        {{ maskAccountNumber(item.bank_account_number) }}
                      </span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Payment Mode</span>
                      <span class="rspv-detail__value">
                        {{ item.payment_mode || "—" }}
                      </span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Amount Paid</span>
                      <span class="rspv-detail__value">
                        {{ currencyOrDash(item.paid_amount) }}
                      </span>
                    </div>
                    <div class="rspv-detail-item">
                      <span class="rspv-detail__label">Finalized On</span>
                      <span class="rspv-detail__value">
                        {{ item.finalized_at | formatDateTime }}
                      </span>
                    </div>
                  </div>

                  <div v-if="item.remarks" class="rspv-remarks">
                    <p class="rspv-remarks__label">Remarks</p>
                    <p class="rspv-remarks__text">{{ item.remarks }}</p>
                  </div>

                  <div v-if="amountsDiffer(item)" class="rspv-empty-txn mt-3">
                    <v-icon small color="info" class="mr-2">mdi-information-outline</v-icon>
                    <span>
                      Paid out {{ currency(item.paid_amount) }}, but this fortnight
                      now totals {{ currency(item.total_amount) }} — invoices landed
                      after the payout.
                    </span>
                  </div>
                </div>

                <div class="rspv-section">
                  <h6 class="rspv-section__title">
                    Programme Breakdown
                    <span v-if="item.split_count">({{ item.split_count }} entries)</span>
                  </h6>

                  <div v-if="!item.split_summary.length" class="rspv-empty-txn">
                    <v-icon small color="grey" class="mr-2">mdi-information-outline</v-icon>
                    <span>
                      The programme-wise breakdown is stored when the split is
                      finalized. {{ item.program_count }} programme(s) and
                      {{ item.invoice_count }} invoice(s) make up this fortnight.
                    </span>
                  </div>

                  <div v-else class="rspv-table-wrap">
                    <table class="rspv-table rspv-table--inner">
                      <thead>
                        <tr>
                          <th class="rspv-th">Program</th>
                          <th class="rspv-th">Fee Type</th>
                          <th class="rspv-th rspv-th--num">Share %</th>
                          <th class="rspv-th rspv-th--num">Base Amount</th>
                          <th class="rspv-th rspv-th--num">GST %</th>
                          <th class="rspv-th rspv-th--num">GST Amount</th>
                          <th class="rspv-th">GST Type</th>
                          <th class="rspv-th rspv-th--num">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(splitItem, sidx) in item.split_summary" :key="sidx">
                          <td class="rspv-td">{{ splitItem.program_name }}</td>
                          <td class="rspv-td">{{ splitItem.fee_type }}</td>
                          <td class="rspv-td rspv-td--num">
                            {{ percent(splitItem.share_percentage) }}
                          </td>
                          <td class="rspv-td rspv-td--num">
                            {{ currency(splitItem.base_amount) }}
                          </td>
                          <td class="rspv-td rspv-td--num">
                            {{ percent(splitItem.gst_percentage) }}
                          </td>
                          <td class="rspv-td rspv-td--num">
                            {{ currency(splitItem.gst_amount) }}
                          </td>
                          <td class="rspv-td">{{ splitItem.split_type }}</td>
                          <td class="rspv-td rspv-td--num rspv-td--strong">
                            {{ currency(splitItem.total_amount) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </td>
          </template>
        </v-data-table>

        <!-- Footer with totals -->
        <div v-if="visiblePeriods.length" class="rspv-footer-totals">
          <span class="rspv-tf">TOTAL ({{ visiblePeriods.length }} fortnights)</span>
          <span class="rspv-tf rspv-tf--strong">{{ currency(visibleTotal) }}</span>
        </div>
      </div>
    </template>

    <!-- Empty State -->
    <v-card v-else outlined class="rspv-state rspv-state--card">
      <v-icon size="44" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <h3 class="rspv-state__title">No revenue found</h3>
      <p class="rspv-state__text">
        There are no paid invoices yet, so there are no revenue splits to show.
      </p>
    </v-card>
  </div>
</template>

<script>
import {
  currency,
  maskAccountNumber,
  monthName,
  num,
  percent,
} from "@/components/revenueSplit/transaction";

export default {
  name: "RevenueSplitPartnerView",

  filters: {
    formatDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN");
    },
    formatDateTime(value) {
      if (!value) return "—";
      return new Date(value).toLocaleString("en-IN");
    },
  },

  data() {
    return {
      loading: false,
      loadingPartners: false,
      periods: [],
      partners: [],
      // What the user is editing vs what the shown result set was built from —
      // the status filter only takes effect on Search, like the partner does.
      form: { partner_id: null, status: "all" },
      applied: { partner_id: null, status: "all" },
      statusOptions: [
        { value: "all", text: "All periods" },
        { value: "pending", text: "Pending" },
        { value: "settled", text: "Paid out" },
      ],
      expandedItems: [],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },

    // Partner-side users only ever see their own splits, so the picker is
    // dropped and the partner comes straight off the logged-in user.
    scopedPartnerId() {
      return this.currentUser?.partner_id || null;
    },

    visiblePeriods() {
      if (this.applied.status === "all") return this.periods;
      return this.periods.filter((p) => p.status === this.applied.status);
    },

    periodsWithKey() {
      return this.visiblePeriods.map((p) => ({
        ...p,
        _key: this.periodKey(p),
      }));
    },

    visibleTotal() {
      return this.visiblePeriods.reduce((sum, p) => sum + num(p.total_amount), 0);
    },

    totals() {
      return this.periods.reduce(
        (acc, period) => {
          const amount = num(period.total_amount);
          acc.revenue += amount;
          if (this.isSettled(period)) {
            acc.settled += amount;
          } else {
            acc.pending += amount;
            acc.pendingCount += 1;
          }
          return acc;
        },
        { revenue: 0, settled: 0, pending: 0, pendingCount: 0 }
      );
    },

    tableHeaders() {
      return [
        { text: "Period", value: "period", align: "left", width: "150px" },
        { text: "Share %", value: "share_percentage", align: "right", width: "80px" },
        { text: "Base Amount", value: "base_amount", align: "right", width: "120px" },
        { text: "GST %", value: "gst_percentage", align: "right", width: "80px" },
        { text: "GST Amount", value: "gst_amount", align: "right", width: "120px" },
        { text: "GST Type", value: "gst_type", align: "left", width: "100px" },
        { text: "Total", value: "partner_share", align: "right", width: "120px" },
        { text: "Status", value: "status", align: "left", width: "100px" },
      ];
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Revenue Split" }]);
    this.initialise();
  },

  methods: {
    currency,
    maskAccountNumber,
    monthName,
    num,
    percent,

    async initialise() {
      if (this.scopedPartnerId) {
        this.form.partner_id = this.scopedPartnerId;
        await this.search();
        return;
      }
      await this.loadPartners();
      const routePartner = this.$route.query.partner_id;
      if (routePartner) {
        this.form.partner_id = Number(routePartner);
        await this.search();
      }
    },

    async loadPartners() {
      this.loadingPartners = true;
      try {
        const { data } = await this.$axios.get("/partners");
        this.partners = data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      } finally {
        this.loadingPartners = false;
      }
    },

    async search() {
      if (!this.form.partner_id) return;
      const partnerChanged = this.applied.partner_id !== this.form.partner_id;
      this.applied = { ...this.form };
      if (partnerChanged) this.expandedItems = [];
      await this.loadPeriods();
    },

    async loadPeriods() {
      if (!this.applied.partner_id) {
        this.periods = [];
        return;
      }

      this.loading = true;
      try {
        const params = new URLSearchParams({ partner_id: this.applied.partner_id });
        const { data } = await this.$axios.get(
          `/revenue_split_histories_finalized?${params}`
        );
        this.periods = (data.data || []).map((period) => ({
          ...period,
          split_summary: period.split_summary || [],
        }));
      } catch (err) {
        this.notifyError(err, "Unable to load revenue splits");
        this.periods = [];
      } finally {
        this.loading = false;
      }
    },

    isSettled(period) {
      return period && period.status === "settled";
    },

    periodKey(period) {
      return `${period.year}-${period.month}-${period.fortnight || 0}`;
    },

    periodTitle(period) {
      const fn = period.fortnight ? ` - FN${period.fortnight}` : "";
      return `${monthName(period.month)} ${period.year}${fn}`;
    },

    currencyOrDash(value) {
      if (value === null || value === undefined || value === "") return "—";
      return currency(value);
    },

    percentOrDash(value) {
      if (value === null || value === undefined || value === "") return "—";
      return percent(value);
    },

    // The payout amount is frozen at finalization; invoices that land later make
    // the fortnight total drift, and finance needs to see that.
    amountsDiffer(period) {
      if (period.paid_amount === null || period.paid_amount === undefined) return false;
      return num(period.paid_amount).toFixed(2) !== num(period.total_amount).toFixed(2);
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || err?.response?.data?.error || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rspv-page {
  --rspv-primary: var(--v-primary-base, #00aff1);
  --rspv-line: #e4e7ec;
  --rspv-ink: #101828;
  --rspv-ink-2: #475467;
  --rspv-ink-3: #98a2b3;
  padding: 16px 40px 32px;
  min-height: 100%;
}

/* Filters */
.rspv-filters {
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  margin-bottom: 16px;
}

.rspv-filters__inner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.rspv-filters__field {
  flex: 0 1 240px;
}

/* Section */
.rspv-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rspv-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rspv-ink);
}

/* Details Grid */
.rspv-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.rspv-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rspv-detail__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rspv-ink-2);
}

.rspv-detail__value {
  font-size: 13px;
  font-weight: 500;
  color: var(--rspv-ink);
}

.rspv-detail__value--code {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 600;
  color: #1565c0;
}

/* Remarks */
.rspv-remarks {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  border-left: 3px solid var(--rspv-primary);
}

.rspv-remarks__label {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--rspv-ink-2);
}

.rspv-remarks__text {
  margin: 0;
  font-size: 12px;
  color: var(--rspv-ink);
  line-height: 1.5;
}

/* Table */
.rspv-table-wrap {
  overflow-x: auto;
}

.rspv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rspv-th {
  padding: 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rspv-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rspv-line);
  white-space: nowrap;
}

.rspv-th--num {
  text-align: right;
}

.rspv-td {
  padding: 10px;
  font-size: 12px;
  color: var(--rspv-ink-2);
  border-bottom: 1px solid #f4f5f7;
}

.rspv-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rspv-td--strong {
  font-weight: 700;
  color: var(--rspv-ink);
}

.rspv-tf {
  padding: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--rspv-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rspv-line);
}

.rspv-tf--strong {
  font-weight: 700;
}

/* States */
.rspv-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 20px;
}

.rspv-state--card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rspv-state__text {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--rspv-ink-2);
}

.rspv-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rspv-ink);
}

/* Details Section */
.rspv-details-section {
  margin-top: 24px;
}

/* Data Table */
.rspv-data-table {
  border: 1px solid var(--rspv-line);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rspv-data-table ::v-deep .v-data-table__wrapper {
  border-radius: 10px;
}

.rspv-data-table ::v-deep .v-data-table th {
  background: #f9fafb !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.6px !important;
  color: var(--rspv-ink-2) !important;
}

.rspv-data-table ::v-deep .v-data-table td {
  font-size: 12px !important;
  color: var(--rspv-ink-2) !important;
}

.rspv-data-table ::v-deep .v-data-table tbody tr:hover {
  background: #f5f5f5 !important;
}

/* Footer Totals */
.rspv-footer-totals {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid var(--rspv-line);
  border-top: none;
  border-radius: 0 0 10px 10px;
  font-weight: 700;
  color: var(--rspv-ink);
  font-size: 13px;
}

.rspv-details-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: var(--rspv-ink);
}

@media (max-width: 768px) {
  .rspv-details-grid {
    grid-template-columns: 1fr;
  }
}

/* Totals strip */
.rspv-totals {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.rspv-total {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 12px 14px;
}

.rspv-total__label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #667085;
}

.rspv-total__value {
  font-size: 18px;
  font-weight: 600;
  color: #101828;
}

.rspv-total__value--success {
  color: #12805c;
}

.rspv-total__value--warning {
  color: #b54708;
}

/* Placeholder shown where a payout or breakdown does not exist yet */
.rspv-empty-txn {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: #475467;
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 6px;
  padding: 10px 12px;
}

.rspv-row__period {
  font-weight: 600;
  color: #101828;
}

.rspv-td--expanded {
  background: #f9fafb;
  padding: 16px 20px !important;
}

.rspv-expanded {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rspv-table--inner {
  background: #fff;
}

.rspv-details-title__count {
  font-size: 12px;
  font-weight: 500;
  color: #98a2b3;
}
</style>
